/**
 * InstructorJournalsPage - 강사 월별 일지 목록 페이지
 *
 * /centers/:centerId/instructors/:instructorId/journals 경로로 접근하며,
 * 선택한 강사의 월별 완료/미작성 일지 목록을 표시한다.
 */

import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { useStudiomateStaffs } from "@/hooks/useStudiomateIntegration";
import {
  getJournalsByInstructor,
  type AdminJournalDocument,
} from "@/services/firestore/admin-journals";
import { fetchAdminStudiomateSchedules } from "@/services/admin-studiomate-schedules";
import { getClassCompletionsBatch } from "@/services/firestore/admin-class-completions";
import {
  settleInstructor,
  previewInstructorSettlement,
  getInstructorSettlement,
  adjustLineItem,
  type SettledInstructorSettlement,
} from "@/services/admin-settlement-actions";
import type { SettlementLineItem } from "@vlab-corporation/vlab-pilates-service-interface/settlement";
import type { StudiomateScheduleItem } from "@vlab-corporation/studiomate-api";
import { SettlementLineItemRow } from "@/components/SettlementLineItemRow";

// --- 월 네비게이션 유틸리티 ---

function getCurrentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getPrevMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  if (month === 1) return `${year - 1}-12`;
  return `${year}-${String(month - 1).padStart(2, "0")}`;
}

function getNextMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  if (month === 12) return `${year + 1}-01`;
  return `${year}-${String(month + 1).padStart(2, "0")}`;
}

function getMonthDateRange(yearMonth: string): { startDate: string; endDate: string } {
  const [year, month] = yearMonth.split("-").map(Number);
  const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { startDate, endDate };
}

// --- 장비 이름 매핑 ---

const EQUIPMENT_LABELS: Record<string, string> = {
  reformer: "리포머",
  cadillac: "캐딜락",
  chair: "체어",
  barrel: "배럴",
  smallEquipment: "소도구",
  mat: "매트",
};

// --- 수업 유형 라벨 ---

function getClassTypeLabel(classType: string | undefined, isOt: boolean | undefined): string {
  if (!classType) return "";
  if (classType === "PRIVATE_REGULAR") return isOt ? "1:1 OT" : "1:1 정규";
  if (classType === "GROUP_REGULAR") return isOt ? "그룹 OT" : "그룹 정규";
  return classType;
}

// --- 미작성 수업 타입 ---

interface PendingClass {
  id: string;
  date: string;
  title: string;
  startTime: string;
  endTime: string;
  staffName: string;
  currentTrainees: number;
  maxTrainees: number;
  isNoShow: boolean;
}

export function InstructorJournalsPage() {
  const { centerId, instructorId } = useParams<{
    centerId: string;
    instructorId: string;
  }>();
  const user = useCurrentUser();

  // Bug 2 fix: yearMonth는 searchParams에서 직접 파생 (로컬 상태 복사 없음)
  // → 새로고침 시에도 URL의 month 파라미터가 그대로 반영된다.
  const [searchParams, setSearchParams] = useSearchParams();
  const yearMonth = searchParams.get("month") ?? getCurrentYearMonth();

  const [journals, setJournals] = useState<AdminJournalDocument[]>([]);
  const [pendingClasses, setPendingClasses] = useState<PendingClass[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 정산 관련 상태
  const [settlement, setSettlement] = useState<SettledInstructorSettlement | null>(null);
  const [isSettling, setIsSettling] = useState(false);
  const [settlementError, setSettlementError] = useState<string | null>(null);
  const [showSettleConfirm, setShowSettleConfirm] = useState(false);
  const [previewData, setPreviewData] = useState<{
    totalAmount: number;
    completedClasses: number;
    lineItems: SettlementLineItem[];
  } | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // 프리뷰 패널 위탁대금 인라인 편집 상태 (idx: 편집 중인 행 인덱스, value: 편집 중인 값)
  const [previewEditingIdx, setPreviewEditingIdx] = useState<number | null>(null);
  const [previewEditingValue, setPreviewEditingValue] = useState<string>("");

  // 완료된 일지 목록 접기/펼치기
  const [isJournalListOpen, setIsJournalListOpen] = useState(false);

  // 라인 아이템 인라인 편집 상태는 SettlementLineItemRow 컴포넌트 내부에서 관리된다.

  // Studiomate staffs를 조회하여 현재 강사의 studiomateStaffId를 찾는다
  const { data: staffsData } = useStudiomateStaffs(Number(centerId));

  const studiomateStaffId = useMemo(() => {
    if (!staffsData?.instructors || !instructorId) return undefined;
    const instructor = staffsData.instructors.find(
      (i) => i.instructorId === instructorId && i.mapping !== null,
    );
    return instructor?.mapping?.studiomateStaffId;
  }, [staffsData, instructorId]);

  const handleSetYearMonth = (month: string) => {
    setSearchParams({ month }, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!centerId || !instructorId) return;

      setIsLoading(true);
      setError(null);

      try {
        // 1. 일지 목록 조회
        const allJournals = await getJournalsByInstructor(user.uid, Number(centerId), instructorId);

        if (cancelled) return;

        // yearMonth로 필터링
        const filteredJournals = allJournals.filter((j) => {
          const date = j.createdAt?.toDate?.();
          if (!date) return false;
          const jYearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
          return jYearMonth === yearMonth;
        });

        // 완료된 일지의 classId 집합
        const completedClassIds = new Set(filteredJournals.map((j) => j.classId));

        // 2. Studiomate 스케줄 조회 (스케줄 데이터로 미작성 감지)
        let pending: PendingClass[] = [];
        try {
          const { startDate, endDate } = getMonthDateRange(yearMonth);
          const scheduleResponse = await fetchAdminStudiomateSchedules(
            Number(centerId),
            startDate,
            endDate,
          );

          if (cancelled) return;

          if (scheduleResponse.schedules) {
            // 강사 식별자: 매핑된 강사는 studiomateStaffId, 미매핑 강사는 instructorId(=userUid).
            // 미매핑 수업은 staff.id=userUid로 기록되므로 항상 이 키로 필터링한다.
            const staffKey: number | string = studiomateStaffId ?? instructorId;
            const instructorSchedules: StudiomateScheduleItem[] = scheduleResponse.schedules.filter(
              (s) => String(s.staffId) === String(staffKey),
            );

            // 과거 수업만 (현재 시간 이전)
            const now = Date.now();
            const pastSchedules = instructorSchedules.filter((s) => {
              const classTime = new Date(`${s.date}T${s.startTime}:00+09:00`).getTime();
              return classTime < now && s.currentTrainees > 0;
            });

            // classCompletions 배치 조회
            const scheduleClassIds = pastSchedules.map((s) => String(s.id));
            const completions =
              scheduleClassIds.length > 0
                ? await getClassCompletionsBatch(user.uid, Number(centerId), scheduleClassIds)
                : new Map();

            if (cancelled) return;

            // 미작성 수업: 일지가 없고 classCompletion에서 journalWritten이 false인 수업
            pending = pastSchedules
              .filter((s) => {
                const classIdStr = String(s.id);
                if (completedClassIds.has(classIdStr)) return false;
                const completion = completions.get(classIdStr);
                // journalWritten이 true이면 이미 완료 (journals에 없을 수도 있지만 completion은 있음)
                if (completion?.journalWritten) return false;
                return true;
              })
              .map((s) => {
                const classIdStr = String(s.id);
                const completion = completions.get(classIdStr);
                return {
                  id: classIdStr,
                  date: s.date,
                  title: s.title,
                  startTime: s.startTime,
                  endTime: s.endTime,
                  staffName: s.staffName,
                  currentTrainees: s.currentTrainees,
                  maxTrainees: s.maxTrainees,
                  isNoShow: completion?.isNoShow ?? false,
                };
              })
              .sort((a, b) => {
                // 날짜+시간 오름차순
                const aKey = `${a.date}T${a.startTime}`;
                const bKey = `${b.date}T${b.startTime}`;
                return aKey.localeCompare(bKey);
              });
          }
        } catch {
          // Studiomate 스케줄 조회 실패 시 미작성 목록은 빈 배열로 유지
          // (일지 목록은 정상 표시)
        }

        // 3. 정산 데이터 조회
        let settlementData: SettledInstructorSettlement | null = null;
        try {
          settlementData = await getInstructorSettlement(
            user.uid,
            Number(centerId),
            yearMonth,
            instructorId,
          );
        } catch {
          // 정산 데이터 조회 실패 시 무시 (정산 미완료로 표시)
        }

        if (!cancelled) {
          setJournals(filteredJournals);
          setPendingClasses(pending);
          setSettlement(settlementData);
          setSettlementError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("일지 데이터 조회 실패"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [user.uid, centerId, instructorId, yearMonth, studiomateStaffId]);

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to={`/centers/${centerId}/instructors`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            &larr; 매핑된 강사 목록
          </Link>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to={`/centers/${centerId}/instructors`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            &larr; 매핑된 강사 목록
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  // 정산 처리 핸들러: "정산 처리" 버튼 클릭 → 확인 패널 표시 + 프리뷰 로딩
  const handleRequestSettle = async () => {
    if (!centerId || !instructorId) return;

    setShowSettleConfirm(true);
    setSettlementError(null);
    setPreviewData(null);
    setPreviewError(null);
    setIsLoadingPreview(true);
    setPreviewEditingIdx(null);

    try {
      const result = await previewInstructorSettlement(Number(centerId), instructorId, yearMonth);
      setPreviewData({
        totalAmount: result.instructorSettlement.totalAmount,
        completedClasses: result.instructorSettlement.completedClasses,
        lineItems: result.instructorSettlement.lineItems,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "정산 금액 계산에 실패했습니다.";
      setPreviewError(message);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // 정산 확인 패널에서 "취소" 클릭
  const handleCancelSettle = () => {
    setShowSettleConfirm(false);
    setPreviewData(null);
    setPreviewError(null);
  };

  // 정산 확인 패널에서 "정산 처리 확인" 클릭 → 실제 정산 실행
  const handleConfirmSettle = async () => {
    if (!centerId || !instructorId) return;

    setIsSettling(true);
    setSettlementError(null);

    try {
      const result = await settleInstructor(Number(centerId), instructorId, yearMonth);

      // 정산 완료 후 데이터 갱신
      const updatedSettlement = await getInstructorSettlement(
        user.uid,
        Number(centerId),
        yearMonth,
        instructorId,
      );
      setSettlement(
        updatedSettlement ??
          ({
            instructorId,
            completedClasses: result.instructorSettlement.completedClasses,
            totalAmount: result.instructorSettlement.totalAmount,
            lineItems: [],
          } as SettledInstructorSettlement),
      );
      setShowSettleConfirm(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "정산 처리에 실패했습니다.";
      setSettlementError(message);
    } finally {
      setIsSettling(false);
    }
  };

  // 라인 아이템 저장 성공 후 정산 데이터 재조회 핸들러
  const handleSettlementAdjusted = () => {
    if (!centerId || !instructorId) return;
    void getInstructorSettlement(user.uid, Number(centerId), yearMonth, instructorId)
      .then((updated) => {
        setSettlement(updated);
      })
      .catch(() => {});
  };

  // 프리뷰 패널 위탁대금 저장: 로컬 previewData만 업데이트 (CF 호출 없음)
  const handlePreviewSaveUnitPrice = (idx: number, parsed: number) => {
    if (previewData === null) return;
    const updatedLineItems = previewData.lineItems.map((li, i) => {
      if (i !== idx) return li;
      return {
        ...li,
        unitPrice: parsed,
        amount: parsed,
        ...(li.originalUnitPrice === undefined ? { originalUnitPrice: li.unitPrice } : {}),
      };
    });
    const newTotalAmount = updatedLineItems.reduce((sum, li) => sum + li.amount, 0);
    setPreviewData({ ...previewData, lineItems: updatedLineItems, totalAmount: newTotalAmount });
    setPreviewEditingIdx(null);
  };

  const isSettled = settlement?.settledAt != null;

  const totalCount = journals.length + pendingClasses.length;

  return (
    <div className="space-y-6">
      {/* 뒤로가기 링크 */}
      <div>
        <Link
          to={`/centers/${centerId}/instructors`}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          &larr; 매핑된 강사 목록
        </Link>
      </div>

      {/* 헤더 + 월 네비게이션 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">강사 일지 목록</h1>
        <p className="text-sm text-gray-500 mt-1">강사 ID: {instructorId}</p>

        <div className="mt-4 flex items-center gap-4">
          <button
            aria-label="이전 달"
            onClick={() => handleSetYearMonth(getPrevMonth(yearMonth))}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            &larr;
          </button>

          <span className="text-lg font-semibold text-gray-900 min-w-[6rem] text-center">
            {yearMonth}
          </span>

          <button
            aria-label="다음 달"
            onClick={() => handleSetYearMonth(getNextMonth(yearMonth))}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            &rarr;
          </button>

          <span className="text-sm text-gray-500">
            총 {totalCount}건 (완료 {journals.length} / 미작성 {pendingClasses.length})
          </span>
        </div>
      </div>

      {/* 완료된 일지 목록 */}
      <div>
        <button
          type="button"
          aria-expanded={isJournalListOpen}
          onClick={() => setIsJournalListOpen((prev) => !prev)}
          className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3 hover:text-gray-700 transition-colors"
        >
          <span
            className={`inline-block transition-transform ${isJournalListOpen ? "rotate-90" : ""}`}
          >
            &#9654;
          </span>
          완료된 일지 목록
          <span className="text-sm font-normal text-gray-500">{journals.length}건</span>
        </button>

        {isJournalListOpen && (
          <>
            {journals.length === 0 ? (
              <div className="bg-white rounded-lg shadow px-6 py-8 text-center text-gray-500">
                {yearMonth}에 작성된 일지가 없습니다.
              </div>
            ) : (
              <div className="space-y-3">
                {journals.map((journal) => {
                  const createdDate = journal.createdAt?.toDate?.();
                  const dateStr = createdDate ? createdDate.toLocaleDateString("ko-KR") : "-";
                  const journalDetailPath = `/centers/${centerId}/instructors/${instructorId}/journals/${journal.id}`;

                  return (
                    <div key={journal.id} className="bg-white rounded-lg shadow">
                      {/* 카드 상단: 날짜/배지 + 회원 상세 링크 */}
                      <div className="flex items-center gap-3 px-6 pt-4">
                        <Link
                          to={journalDetailPath}
                          className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                        >
                          <span className="font-medium text-gray-900">{dateStr}</span>
                          {journal.classType && (
                            <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {getClassTypeLabel(journal.classType, journal.isOt)}
                            </span>
                          )}
                          <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            완료
                          </span>
                        </Link>
                        {/* studiomateMemberId 또는 memberId가 있으면 회원 상세 링크를 카드 우측에 표시 */}
                        {(journal.studiomateMemberId ?? journal.memberId) && (
                          <Link
                            to={`/centers/${centerId}/members/${journal.studiomateMemberId ?? journal.memberId}`}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                          >
                            회원 상세 &rarr;
                          </Link>
                        )}
                      </div>

                      {/* 카드 하단: 기구/문제 목록/운동 수 - 일지 상세로 이동 */}
                      <Link
                        to={journalDetailPath}
                        className="block px-6 pb-4 hover:bg-blue-50 transition-colors cursor-pointer"
                      >
                        {journal.equipments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {journal.equipments.map((eq) => (
                              <span
                                key={eq}
                                className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                              >
                                {EQUIPMENT_LABELS[eq] ?? eq}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="mt-2 text-sm text-gray-700 line-clamp-2">
                          {journal.problemList}
                        </div>

                        {journal.exercises.length > 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            운동 {journal.exercises.length}개
                          </div>
                        )}
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* 정산 섹션 */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">정산</h2>

        {isSettled ? (
          /* 정산 완료 상태 */
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                정산 완료
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-500">정산 금액 (VAT 포함)</span>
                <p className="font-semibold text-gray-900 mt-0.5">
                  {settlement.totalAmount.toLocaleString("ko-KR")}원
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  세후{" "}
                  {(
                    settlement.totalAmount - Math.round(settlement.totalAmount * 0.033)
                  ).toLocaleString("ko-KR")}
                  원
                </p>
              </div>
              <div>
                <span className="text-gray-500">완료 수업 수</span>
                <p className="font-semibold text-gray-900 mt-0.5">
                  {settlement.completedClasses}건
                </p>
              </div>
              {settlement.settledAt && (
                <div>
                  <span className="text-gray-500">정산 일시</span>
                  <p className="font-semibold text-gray-900 mt-0.5">
                    {"toDate" in settlement.settledAt &&
                    typeof settlement.settledAt.toDate === "function"
                      ? settlement.settledAt.toDate().toLocaleString("ko-KR")
                      : "-"}
                  </p>
                </div>
              )}
            </div>

            {/* 라인 아이템 테이블 */}
            {settlement.lineItems.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">수업별 정산 내역</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 text-xs">
                        <th className="px-3 py-2 text-left">수업일</th>
                        <th className="px-3 py-2 text-left">회원명</th>
                        <th className="px-3 py-2 text-left">회원 유형</th>
                        <th className="px-3 py-2 text-right">위탁대금</th>
                        <th className="px-3 py-2 text-right">금액 (VAT 포함)</th>
                        <th className="px-3 py-2 text-left">비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlement.lineItems.map((item, idx) => (
                        // 정산 상태가 "calculating"일 때만 인라인 편집 활성화
                        <SettlementLineItemRow
                          key={idx}
                          item={item}
                          centerId={Number(centerId)}
                          instructorId={instructorId ?? ""}
                          yearMonth={yearMonth}
                          isEditable={true}
                          onAdjust={adjustLineItem}
                          onAdjusted={handleSettlementAdjusted}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : showSettleConfirm ? (
          /* 정산 확인 패널 */
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-base font-semibold text-blue-900 mb-2">정산 확인</h3>
            <p className="text-sm text-blue-800 mb-1">
              <span className="font-semibold">{journals.length}건</span>의 완료된 일지에 대해 정산을
              진행합니다.
            </p>

            {/* 프리뷰 로딩/에러/결과 */}
            {isLoadingPreview ? (
              <p className="text-sm text-blue-600 my-3">정산 금액 계산 중...</p>
            ) : previewError ? (
              <div className="my-3 text-sm text-red-600">{previewError}</div>
            ) : previewData ? (
              <div className="my-3">
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <span className="text-blue-700">예상 정산 금액</span>
                    <p className="font-bold text-blue-900 text-lg mt-0.5">
                      {previewData.totalAmount.toLocaleString("ko-KR")}원
                    </p>
                  </div>
                  <div>
                    <span className="text-blue-700">완료 수업 수</span>
                    <p className="font-semibold text-blue-900 mt-0.5">
                      {previewData.completedClasses}건
                    </p>
                  </div>
                </div>
                {previewData.lineItems.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-blue-100 text-blue-700 text-xs">
                          <th className="px-3 py-1.5 text-left">수업일</th>
                          <th className="px-3 py-1.5 text-left">회원명</th>
                          <th className="px-3 py-1.5 text-left">회원 유형</th>
                          <th className="px-3 py-1.5 text-right">정산 위탁대금</th>
                          <th className="px-3 py-1.5 text-center">비고</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.lineItems.map((item, idx) => (
                          <tr key={idx} className="border-t border-blue-100">
                            <td className="px-3 py-1.5 text-blue-800">
                              {item.classDate ? item.classDate.slice(5).replace("-", ".") : "-"}
                            </td>
                            <td className="px-3 py-1.5 text-blue-800">
                              {item.memberName ? (
                                (item.studiomateMemberId ?? item.memberId) ? (
                                  <Link
                                    to={`/centers/${centerId}/members/${item.studiomateMemberId ?? item.memberId}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {item.memberName}
                                  </Link>
                                ) : (
                                  item.memberName
                                )
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-blue-800">
                              {item.memberType === null ? (
                                <span className="text-gray-400">N/A</span>
                              ) : item.memberType === "firstPurchase" ? (
                                "첫 구매"
                              ) : (
                                "재등록"
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right font-medium text-blue-900">
                              {previewEditingIdx === idx ? (
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    type="number"
                                    aria-label="정산 위탁대금 입력"
                                    value={previewEditingValue}
                                    onChange={(e) => setPreviewEditingValue(e.target.value)}
                                    className="w-24 px-2 py-0.5 text-right text-sm border border-blue-300 rounded"
                                  />
                                  <button
                                    aria-label="정산 위탁대금 저장"
                                    onClick={() => {
                                      const parsed = parseInt(previewEditingValue, 10);
                                      if (!isNaN(parsed)) {
                                        handlePreviewSaveUnitPrice(idx, parsed);
                                      } else {
                                        setPreviewEditingIdx(null);
                                      }
                                    }}
                                    className="px-1.5 py-0.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                  >
                                    저장
                                  </button>
                                  <button
                                    aria-label="정산 위탁대금 편집 취소"
                                    onClick={() => setPreviewEditingIdx(null)}
                                    className="px-1.5 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                                  >
                                    취소
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-1">
                                  <span>{item.amount.toLocaleString("ko-KR")}원</span>
                                  <button
                                    aria-label="정산 위탁대금 편집"
                                    onClick={() => {
                                      setPreviewEditingIdx(idx);
                                      setPreviewEditingValue(String(item.amount));
                                    }}
                                    className="p-0.5 text-blue-400 hover:text-blue-600 rounded hover:bg-blue-100"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="13"
                                      height="13"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden="true"
                                    >
                                      <line x1="18" y1="2" x2="22" y2="6" />
                                      <path d="M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              {item.isNoShow && (
                                <span className="inline-block px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  노쇼
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleCancelSettle}
                disabled={isSettling}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
              <button
                onClick={() => void handleConfirmSettle()}
                disabled={isSettling || isLoadingPreview || previewError != null}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSettling ? "처리 중..." : "정산 처리 확인"}
              </button>
            </div>
            {settlementError && <div className="mt-3 text-sm text-red-600">{settlementError}</div>}
          </div>
        ) : (
          /* 미정산 상태 */
          <div className="bg-white rounded-lg shadow p-6">
            {journals.length === 0 ? (
              <p className="text-gray-500 text-sm">완료된 일지가 없어 정산할 수 없습니다.</p>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  정산 대상: 완료된 일지 <span className="font-semibold">{journals.length}건</span>
                </div>
                <button
                  onClick={handleRequestSettle}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  정산 처리
                </button>
              </div>
            )}
            {settlementError && <div className="mt-3 text-sm text-red-600">{settlementError}</div>}
          </div>
        )}
      </div>

      {/* 미작성 일지 목록 */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          미작성 일지 목록
          <span className="ml-2 text-sm font-normal text-gray-500">{pendingClasses.length}건</span>
        </h2>

        {pendingClasses.length === 0 ? (
          <div className="bg-white rounded-lg shadow px-6 py-8 text-center text-gray-500">
            {yearMonth}에 미작성 일지가 없습니다.
          </div>
        ) : (
          <div className="space-y-3">
            {pendingClasses.map((cls) => (
              <div
                key={cls.id}
                className="bg-white rounded-lg shadow px-6 py-4 border-l-4 border-orange-300"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{cls.date}</span>
                  <span className="text-sm text-gray-600">
                    {cls.startTime} - {cls.endTime}
                  </span>
                  {cls.isNoShow ? (
                    <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      노쇼
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                      미작성
                    </span>
                  )}
                </div>

                <div className="mt-1 text-sm text-gray-700">{cls.title}</div>

                <div className="mt-1 text-xs text-gray-500">
                  수강생 {cls.currentTrainees}/{cls.maxTrainees}명
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
